// src/receptionist/screens/Staff.tsx
//
// Where the AI hands work to people: the staff WhatsApp group it posts into,
// which alerts it sends there, who can see the console, and the rule that
// stops it talking over a human mid-thread (PRD §3.6).

import { useCallback, useEffect, useState } from "react";
import { CheckIcon, CopyIcon, ExternalLinkIcon, PencilIcon, SendIcon, TrashIcon, UsersRoundIcon } from "lucide-react";
import { PageHeader, Card, CardHeader, CardBody, Button } from "../components/kit";
import { AddButton, TagPill, WaPreview } from "../components/ui";
import { Switch } from "@component/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@component/dialog";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@component/select";
import { getStaffChannel, sendStaffChannelTest, type StaffChannelState } from "@service/receptionist";
import type { MerchantConfig, StaffMember } from "../merchant";
import type { MerchantScreenProps } from "../merchantState";

/** `2 hours ago`, `3 days ago`. Null reads as never. */
function sinceLabel(iso: string | null): string {
    if (!iso) return "never";
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
}

/** The link the merchant forwards. Selectable as well as copyable — clipboard
 *  access is denied in an insecure context and the link still has to be gettable. */
/** A Malaysian number as wa.me wants it ("012-345 6789" -> "60123456789"), or null. */
function waTo(phone: string): string | null {
    const digits = (phone || "").replace(/\D/g, "");
    if (!digits) return null;
    if (digits.startsWith("60")) return digits;
    if (digits.startsWith("0")) return `60${digits.slice(1)}`;
    return digits.length >= 8 ? digits : null;
}

/** What a teammate is sent to connect: their first name, and the link that opens a chat with the number. */
function connectMessage(name: string, link: string): string {
    const first = name.trim().split(/\s+/)[0] || "there";
    return `Hi ${first}, please tap this link and press Send, so you get our booking alerts on WhatsApp: ${link}`;
}

/** Copies `value`; says Copied for a moment. Fails quietly where the clipboard is refused. */
function CopyButton({ value, label }: { value: string; label: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            /* insecure context or denied permission */
        }
    };
    return (
        <Button variant="outline" onClick={() => void copy()}>
            {copied ? <><CheckIcon className="h-4 w-4" /> Copied</> : <><CopyIcon className="h-4 w-4" /> {label}</>}
        </Button>
    );
}

function ConnectLink({ value }: { value: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            /* insecure context or denied permission; the field is still selectable */
        }
    };
    return (
        <div className="flex items-center gap-2">
            <Input
                readOnly
                value={value}
                onFocus={(e) => e.currentTarget.select()}
                className="bg-white font-mono text-xs"
                aria-label="Staff connect link"
            />
            <Button size="sm" onClick={() => void copy()} className="flex-shrink-0">
                {copied ? <><CheckIcon className="h-4 w-4" /> Copied</> : <><CopyIcon className="h-4 w-4" /> Copy</>}
            </Button>
        </div>
    );
}

/** The product's switch. */
function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
    return <Switch checked={on} onCheckedChange={onChange} aria-label={label} className="flex-shrink-0" />;
}

function Row({ title, hint, control }: { title: string; hint?: string; control: React.ReactNode }) {
    return (
        <div className="flex items-center gap-4 border-t border-gray-100 py-3 first:border-t-0">
            <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{title}</p>
                {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
            </div>
            {control}
        </div>
    );
}

export function StaffScreen({ merchant: server, onSave }: MerchantScreenProps) {
    // A local draft of the server's copy. Whenever the prop changes — after a
    // save returned the server's object, or after a failed save re-synced —
    // the draft is replaced wholesale, so the form never shows an optimistic
    // copy the server did not accept.
    const [m, setM] = useState<MerchantConfig>(server);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    // Both of these edit the merchant config in place; the page's own
    // "Save changes" is what persists them. The group's connected flag and
    // member count stay the server's — a client cannot set those.
    const [groupDraft, setGroupDraft] = useState<string | null>(null);
    /** The person being added, or edited when it carries an `id`. */
    const [personDraft, setPersonDraft] = useState<(Omit<StaffMember, "id"> & { id?: string }) | null>(null);
    /** The person whose removal is being confirmed. */
    const [removing, setRemoving] = useState<StaffMember | null>(null);
    /** The person the connect link is being sent to. */
    const [linkFor, setLinkFor] = useState<StaffMember | null>(null);
    /**
     * Who we can actually reach (ourteam-backend#4711). Separate from
     * `MerchantConfig.staffChannel`, which carries only the name and the
     * roll-up: this is the per-member truth plus the connect link, and the link
     * is the only thing that moves a merchant off zero.
     */
    const [channel, setChannel] = useState<StaffChannelState | null>(null);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<string | null>(null);

    const loadChannel = useCallback(async () => {
        try {
            setChannel(await getStaffChannel(server.id));
        } catch {
            // An older backend has no such route. The card falls back to the
            // config's own staffChannel, so the screen still renders.
            setChannel(null);
        }
    }, [server.id]);
    useEffect(() => { void loadChannel(); }, [loadChannel]);

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const r = await sendStaffChannelTest(server.id);
            setTestResult(
                r.ok
                    ? r.delivered
                        ? `Sent to ${r.memberCount} ${r.memberCount === 1 ? "person" : "people"}.`
                        : "Nothing was delivered. Check the numbers on your team."
                    : r.message,
            );
            await loadChannel();
        } finally {
            setTesting(false);
        }
    };
    useEffect(() => { setM(server); setDirty(false); setGroupDraft(null); setPersonDraft(null); setRemoving(null); setLinkFor(null); }, [server]);
    const save = async () => {
        setSaving(true);
        try { if (await onSave(m)) setDirty(false); } finally { setSaving(false); }
    };
    const patch = (next: Partial<MerchantConfig>) => { setM((p) => ({ ...p, ...next })); setDirty(true); };
    /** The live endpoint is the truth; the config's flag is the fallback for a
     *  backend that predates the staff-channel route. */
    const connectedNow = channel ? channel.connected : m.staffChannel.connected;

    return (
        <div>
            <PageHeader
                title="Staff"
                subtitle="Where booking requests land, who gets told, and how a human takes over a chat."
                status={`${m.people.length} on the team · ${connectedNow ? "Staff channel connected" : "Staff channel not connected"}`}
                action={<Button disabled={!dirty || saving} onClick={() => void save()}>{saving ? "Saving…" : "Save changes"}</Button>}
            />

            <Card className="mb-5">
                <CardHeader
                    title="Staff channel"
                    icon={UsersRoundIcon}
                    description="Where booking requests and escalations reach your team. WhatsApp has no group API, so the AI messages each person on the list individually and they reply with a number to action a request."
                    aside={
                        connectedNow ? (
                            <TagPill tone="green">
                                {channel ? `${channel.memberCount} of ${channel.invitedCount} connected` : "Connected"}
                            </TagPill>
                        ) : (
                            <TagPill tone="amber">Not connected</TagPill>
                        )
                    }
                />
                <CardBody className="pt-0">
                    {connectedNow ? (
                        <>
                            <p className="text-sm text-gray-700">
                                {m.staffChannel.name || "Your team"}
                                {channel ? ` · ${channel.memberCount} of ${channel.invitedCount} reachable` : ""}
                            </p>
                            {channel && channel.memberCount < channel.invitedCount && channel.connectLink && (
                                <div className="mt-3 space-y-2 rounded-xl bg-gray-50 p-3">
                                    <p className="text-xs text-gray-600">
                                        {channel.invitedCount - channel.memberCount} on your team{" "}
                                        {channel.invitedCount - channel.memberCount === 1 ? "has" : "have"} not messaged
                                        the number yet, so alerts cannot reach them. Forward this link.
                                    </p>
                                    <ConnectLink value={channel.connectLink} />
                                </div>
                            )}
                            {groupDraft === null ? (
                                <div className="mt-3 flex gap-2">
                                    <Button variant="secondary" size="sm" disabled={testing} onClick={() => void runTest()}>
                                        <SendIcon className="h-4 w-4" />
                                        {testing ? "Sending…" : "Send a test message"}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => setGroupDraft(m.staffChannel.name)}>
                                        Change group
                                    </Button>
                                </div>
                            ) : (
                                <div className="mt-3 space-y-2 rounded-xl bg-gray-50 p-3">
                                    <Label htmlFor="rcp-staff-group" className="text-sm font-medium">Staff group name</Label>
                                    <Input
                                        id="rcp-staff-group"
                                        autoFocus
                                        value={groupDraft}
                                        placeholder="e.g. Front desk team"
                                        onChange={(e) => setGroupDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && groupDraft.trim()) {
                                                patch({ staffChannel: { ...m.staffChannel, name: groupDraft.trim() } });
                                                setGroupDraft(null);
                                            }
                                            if (e.key === "Escape") setGroupDraft(null);
                                        }}
                                        className="bg-white"
                                    />
                                    <p className="text-xs text-gray-500">
                                        The name the console shows for your group. Which WhatsApp group the AI actually posts
                                        into is set during onboarding.
                                    </p>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            disabled={!groupDraft.trim()}
                                            onClick={() => {
                                                patch({ staffChannel: { ...m.staffChannel, name: groupDraft.trim() } });
                                                setGroupDraft(null);
                                            }}
                                        >
                                            Update name
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => setGroupDraft(null)}>Cancel</Button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        // Not connected: a member count here would be a number about
                        // nothing. The group is where every booking request lands, so
                        // this is the one thing to do before anything else works.
                        <div className="rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200">
                            <p className="text-sm font-medium text-amber-900">No staff group connected yet</p>
                            <p className="mt-0.5 text-xs text-amber-800">
                                Booking requests, escalations and the morning digest all go here. Your team connects
                                itself: each person sends one message to your WhatsApp number, which proves the number
                                reaches them and opens the 24-hour window an alert needs. Forward this link and ask
                                them to send it.
                            </p>
                            {channel?.connectLink && (
                                <div className="mt-2.5">
                                    <ConnectLink value={channel.connectLink} />
                                </div>
                            )}
                            {channel && channel.invitedCount === 0 && (
                                <p className="mt-2 text-xs text-amber-800">
                                    Add your team below first — the link is only useful to someone on the list.
                                </p>
                            )}
                            {groupDraft === null ? (
                                <Button size="sm" className="mt-2" onClick={() => setGroupDraft(m.staffChannel.name)}>
                                    Name your group
                                </Button>
                            ) : (
                                <div className="mt-3 space-y-2 rounded-xl bg-white p-3 ring-1 ring-inset ring-black/[0.06]">
                                    <Label htmlFor="rcp-staff-group-new" className="text-sm font-medium">Staff group name</Label>
                                    <Input
                                        id="rcp-staff-group-new"
                                        autoFocus
                                        value={groupDraft}
                                        placeholder="e.g. Front desk team"
                                        onChange={(e) => setGroupDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && groupDraft.trim()) {
                                                patch({ staffChannel: { ...m.staffChannel, name: groupDraft.trim() } });
                                                setGroupDraft(null);
                                            }
                                            if (e.key === "Escape") setGroupDraft(null);
                                        }}
                                    />
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            disabled={!groupDraft.trim()}
                                            onClick={() => {
                                                patch({ staffChannel: { ...m.staffChannel, name: groupDraft.trim() } });
                                                setGroupDraft(null);
                                            }}
                                        >
                                            Save name
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => setGroupDraft(null)}>Cancel</Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {testResult && (
                        <p className="mt-3 text-xs text-gray-600">{testResult}</p>
                    )}
                </CardBody>
            </Card>

            <div className="grid gap-5 md:grid-cols-2">
                <Card>
                    <CardHeader title="Alerts" description="What the AI posts to the staff channel." />
                    <CardBody className="pt-0">
                        <Row
                            title="New booking request"
                            hint="Posted the moment a request is complete."
                            control={<Toggle on={m.alertOnBooking} label="Booking alerts" onChange={(v) => patch({ alertOnBooking: v })} />}
                        />
                        <Row
                            title="Escalation"
                            hint="With the customer's question and the thread so far."
                            control={<Toggle on={m.alertOnEscalation} label="Escalation alerts" onChange={(v) => patch({ alertOnEscalation: v })} />}
                        />
                        <Row
                            title="Morning digest"
                            hint="Overnight enquiries, oldest first."
                            control={<Toggle on={m.alertMorningDigest} label="Morning digest" onChange={(v) => patch({ alertMorningDigest: v })} />}
                        />
                        <Row
                            title="Chase an unanswered request"
                            hint="Reposts a pending request during business hours."
                            control={
                                <Select
                                    value={m.unconfirmedChaseHours === null ? "never" : String(m.unconfirmedChaseHours)}
                                    onValueChange={(v) => patch({ unconfirmedChaseHours: v === "never" ? null : Number(v) })}
                                >
                                    <SelectTrigger className="w-40 border border-gray-300 dark:border-gray-700"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {/* "never" rather than "" — an empty SelectItem value is
                                            not selectable, and null is a real setting here. */}
                                        <SelectItem value="never">Never</SelectItem>
                                        <SelectItem value="1">After 1 hour</SelectItem>
                                        <SelectItem value="2">After 2 hours</SelectItem>
                                        <SelectItem value="4">After 4 hours</SelectItem>
                                    </SelectContent>
                                </Select>
                            }
                        />
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader title="People" description="Who the AI messages, and who can open this console." />
                    <CardBody className="pt-0">
                        {m.people.map((p) => {
                            // Three states, not two. Never heard from means alerts
                            // cannot reach them at all; a lapsed window still takes
                            // an approved template, which is a different problem.
                            const reach = channel?.members.find((x) => x.id === p.id);
                            const reachHint = !reach
                                ? null
                                : !reach.connected
                                  ? "Has not messaged yet, so alerts cannot reach them"
                                  : reach.windowOpen
                                    ? `Reachable · last message ${sinceLabel(reach.lastSeenAt)}`
                                    : `Window closed since ${sinceLabel(reach.lastSeenAt)} · needs a template`;
                            const canSendLink = Boolean(reach && !reach.connected && channel?.connectLink);
                            return (
                                <div key={p.id} className="flex flex-wrap items-start gap-x-3 gap-y-2 border-t border-gray-100 py-3 first:border-t-0">
                                    <div className="min-w-[12rem] flex-1">
                                        <p className="text-sm font-medium text-gray-900">
                                            {p.name} <span className="font-normal text-gray-500">· {p.role}</span>
                                        </p>
                                        {/* The number with the person it belongs to, not beside the delete button (29 Oct 2026). */}
                                        <p className="mt-0.5 text-xs tabular-nums text-gray-600">
                                            {p.phone} · {p.consoleAccess ? "Console access" : "Staff alerts only"}
                                        </p>
                                        {reachHint && <p className="mt-0.5 text-xs text-gray-500">{reachHint}</p>}
                                    </div>
                                    <div className="ml-auto flex flex-shrink-0 items-center gap-1">
                                        {/* Not heard from yet: a dialog with the message and the link to send them (#4796). */}
                                        {canSendLink && (
                                            <button
                                                type="button"
                                                onClick={() => setLinkFor(p)}
                                                className="mr-1 inline-flex h-7 items-center gap-1.5 rounded-full bg-white px-3 text-xs font-medium text-gray-900 ring-1 ring-inset ring-black/[0.08] hover:bg-gray-50"
                                            >
                                                <SendIcon className="h-3.5 w-3.5" /> Send link
                                            </button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={`Edit ${p.name}`}
                                            title="Edit"
                                            className="h-8 w-8 text-gray-400 hover:text-gray-700"
                                            onClick={() => setPersonDraft({ id: p.id, name: p.name, role: p.role, phone: p.phone, consoleAccess: p.consoleAccess })}
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={`Remove ${p.name}`}
                                            title="Remove"
                                            className="h-8 w-8 text-gray-400 hover:text-red-600"
                                            onClick={() => setRemoving(p)}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="mt-3">
                            <AddButton onClick={() => setPersonDraft({ name: "", role: "", phone: "", consoleAccess: false })}>
                                Add person
                            </AddButton>
                        </div>

                        {/* Add or edit, in a dialog with its own Cancel: the card is the list of who is on the team. */}
                        <Dialog open={personDraft !== null} onOpenChange={(o) => { if (!o) setPersonDraft(null); }}>
                            <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>{personDraft?.id ? `Edit ${m.people.find((x) => x.id === personDraft.id)?.name ?? "this person"}` : "Add someone to the team"}</DialogTitle>
                                    <DialogDescription>
                                        They get the staff group messages. Console access is what lets them open
                                        this console and act on a booking.
                                    </DialogDescription>
                                </DialogHeader>
                                {personDraft !== null && (
                                    <div className="space-y-3">
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="space-y-1.5">
                                            <Label htmlFor="rcp-person-name" className="text-sm font-medium">Name</Label>
                                            <Input
                                                id="rcp-person-name"
                                                autoFocus
                                                value={personDraft.name}
                                                placeholder="e.g. Aina"
                                                onChange={(e) => setPersonDraft({ ...personDraft, name: e.target.value })}
                                                className="bg-white"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label htmlFor="rcp-person-role" className="text-sm font-medium">Role</Label>
                                            <Input
                                                id="rcp-person-role"
                                                value={personDraft.role}
                                                placeholder="e.g. Reception"
                                                onChange={(e) => setPersonDraft({ ...personDraft, role: e.target.value })}
                                                className="bg-white"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="rcp-person-phone" className="text-sm font-medium">WhatsApp number</Label>
                                        <Input
                                            id="rcp-person-phone"
                                            value={personDraft.phone}
                                            placeholder="e.g. 012-345 6789"
                                            onChange={(e) => setPersonDraft({ ...personDraft, phone: e.target.value })}
                                            className="bg-white"
                                        />
                                    </div>
                                    <div className="flex items-start gap-3 pt-1">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-900">Console access</p>
                                            <p className="text-xs text-gray-500">Off means they only get the staff group messages.</p>
                                        </div>
                                        <Switch
                                            checked={personDraft.consoleAccess}
                                            onCheckedChange={(v) => setPersonDraft({ ...personDraft, consoleAccess: v })}
                                            aria-label="Console access"
                                            className="mt-0.5 flex-shrink-0"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500">Nothing is sent until you press Save changes.</p>
                                    </div>
                                )}
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setPersonDraft(null)}>Cancel</Button>
                                    <Button
                                        disabled={personDraft === null || !personDraft.name.trim() || !personDraft.phone.trim()}
                                        onClick={() => {
                                            if (personDraft === null) return;
                                            const fields = {
                                                name: personDraft.name.trim(),
                                                role: personDraft.role.trim() || "Staff",
                                                phone: personDraft.phone.trim(),
                                                consoleAccess: personDraft.consoleAccess,
                                            };
                                            const editing = personDraft.id;
                                            patch({
                                                people: editing
                                                    ? m.people.map((x) => (x.id === editing ? { ...x, ...fields } : x))
                                                    : [...m.people, { ...fields, id: `p_${Math.random().toString(36).slice(2, 8)}` }],
                                            });
                                            setPersonDraft(null);
                                        }}
                                    >
                                        {personDraft?.id ? "Update" : "Add to the team"}
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* Removing someone asks first: one stray press used to take them off the team. */}
                        <Dialog open={removing !== null} onOpenChange={(o) => { if (!o) setRemoving(null); }}>
                            <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>Remove {removing?.name ?? "this person"}?</DialogTitle>
                                    <DialogDescription>
                                        They stop getting staff alerts{removing?.consoleAccess ? " and lose console access" : ""}.
                                        This takes effect when you press Save changes.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setRemoving(null)}>Cancel</Button>
                                    <Button
                                        className="bg-red-600 text-white hover:bg-red-700"
                                        onClick={() => {
                                            if (removing) patch({ people: m.people.filter((x) => x.id !== removing.id) });
                                            setRemoving(null);
                                        }}
                                    >
                                        <TrashIcon className="h-4 w-4" /> Remove
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* The connect link, with the message it goes out in: copy either, or open WhatsApp to them.
                            The link has no spaces: it breaks where it must, the words around it do not. */}
                        <Dialog open={linkFor !== null} onOpenChange={(o) => { if (!o) setLinkFor(null); }}>
                            <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>Send the connect link to {linkFor?.name ?? "them"}</DialogTitle>
                                    <DialogDescription>
                                        They tap the link and press Send in WhatsApp. That one message is what lets the AI
                                        reach them with booking alerts.
                                    </DialogDescription>
                                </DialogHeader>
                                {linkFor && channel?.connectLink && (() => {
                                    const message = connectMessage(linkFor.name, channel.connectLink);
                                    const to = waTo(linkFor.phone);
                                    return (
                                        <div className="space-y-3">
                                            <div className="min-w-0 [&_p]:[overflow-wrap:anywhere]">
                                                <WaPreview text={message} note={`What ${linkFor.name.split(" ")[0]} receives`} />
                                            </div>
                                            <div className="space-y-1.5">
                                                <Label className="text-sm font-medium">Connect link</Label>
                                                <ConnectLink value={channel.connectLink} />
                                            </div>
                                            <div className="flex flex-wrap justify-end gap-2">
                                                <CopyButton value={message} label="Copy message" />
                                                {to && (
                                                    <a
                                                        href={`https://wa.me/${to}?text=${encodeURIComponent(message)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[#0A5FD1] px-4 text-sm font-medium text-white hover:bg-[#0952B8]"
                                                    >
                                                        <ExternalLinkIcon className="h-4 w-4" /> Open WhatsApp to {linkFor.phone}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </DialogContent>
                        </Dialog>
                    </CardBody>
                </Card>
            </div>

            <Card className="mt-5">
                <CardHeader title="Taking over a chat" description="So the AI never talks over one of your team." />
                <CardBody className="pt-0">
                    <p className="text-sm text-gray-700">
                        When someone from your team replies inside a customer thread, the AI goes quiet there.
                        It starts again when they send <b>{m.handBackKeyword}</b> in the staff channel, or after{" "}
                        {m.handBackQuietHours} quiet hours.
                    </p>
                </CardBody>
            </Card>
        </div>
    );
}
